import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { Save, Building2, Upload, Image as ImageIcon, KeyRound, Mail } from "lucide-react";
import { DocTemplatesCard } from "../components/DocTemplatesCard";
import { MaterialNormsCard } from "../components/MaterialNormsCard";
import { toast } from "sonner";
import { VatRatePicker } from "../components/VatRatePicker";
import { RolesTable } from "../components/RolesTable";
import { InvoicingSettings } from "../components/InvoicingSettings";
import { VehiclesCard } from "../components/VehiclesCard";
import { TravelRatesCard } from "../components/TravelRatesCard";
import { BackupCard } from "../components/BackupCard";
import { HistoryPanel } from "../components/audit/AuditList";
import { Link } from "react-router-dom";

const EMPTY = { name: "", ico: "", dic: "", address: "", city: "", zip: "", phone: "", email: "", bank_account: "", iban: "", logo_path: "", vat_rate: 21, vat_payer: true };

export default function Settings() {
    const [company, setCompany] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const fileRef = useRef(null);

    const load = () => api.get("/company").then((r) => { if (r.data) setCompany(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    const set = (k) => (e) => setCompany({ ...company, [k]: e.target.value });

    const [bankError, setBankError] = useState("");
    const save = async () => {
        try {
            const payload = {
                name: company.name, ico: company.ico, dic: company.dic, address: company.address,
                city: company.city, zip: company.zip, phone: company.phone, email: company.email,
                bank_account: company.bank_account, iban: company.iban || "",
                logo_path: company.logo_path || "", vat_rate: Number(company.vat_rate) || 21,
                vat_payer: company.vat_payer !== false,
                stamp_path: company.stamp_path || "", registration_text: company.registration_text || "", web: company.web || "",
                bank_name: company.bank_name || "", swift: company.swift || "", default_due_days: Number(company.default_due_days ?? 7), default_craftsman_share_pct: Number(company.default_craftsman_share_pct ?? 51),
                default_vat_mode: company.default_vat_mode || "",
                offer_footer_text: company.offer_footer_text ?? null, surcharge_defaults: company.surcharge_defaults ?? null,
            };
            const { data } = await api.put("/company", payload);
            setCompany(data);
            setBankError("");
            toast.success("Údaje firmy uloženy");
        } catch (e) {
            const msg = e.response?.data?.detail || "Uložení selhalo";
            if (/IBAN|účtu/i.test(msg)) setBankError(msg);
            toast.error(msg);
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCompany({ ...company, logo_path: data.logo_path });
            toast.success("Logo nahráno");
        } catch { toast.error("Nahrání selhalo"); }
    };

    if (loading) return <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání…</div>;

    return (
        <div className="px-10 py-10 max-w-3xl mx-auto animate-fade-in" data-testid="settings-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Nastavení</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Údaje firmy</h1>
                    <p className="text-sm text-slate-600 mt-2">Tyto údaje + logo se zobrazí ve všech PDF dokumentech.</p>
                </div>
                <button onClick={save} data-testid="save-company"
                        className="px-5 py-2.5 text-sm uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm inline-flex items-center gap-2">
                    <Save className="w-4 h-4" /> Uložit
                </button>
            </div>

            {/* Logo */}
            <div className="bg-white border border-slate-200 sharp-shadow mb-6">
                <div className="flex items-center gap-3 px-6 py-4 border-b-2 border-slate-900">
                    <ImageIcon className="w-5 h-5 text-orange-600" />
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Logo firmy</div>
                </div>
                <div className="p-6 flex items-center gap-6">
                    <div className="w-32 h-20 border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {company.logo_path ? (
                            <img src={fileUrl(company.logo_path)} alt="Logo" className="max-w-full max-h-full object-contain" />
                        ) : (
                            <ImageIcon className="w-8 h-8 text-slate-300" />
                        )}
                    </div>
                    <div className="flex-1">
                        <button onClick={() => fileRef.current?.click()} data-testid="upload-logo"
                                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">
                            <Upload className="w-3.5 h-3.5" /> {company.logo_path ? "Změnit logo" : "Nahrát logo"}
                        </button>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                               onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
                        <p className="text-xs text-slate-500 mt-2">PNG, JPG nebo WEBP. Doporučená velikost 400×200 px.</p>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 sharp-shadow">
                <div className="flex items-center gap-3 px-6 py-4 border-b-2 border-slate-900">
                    <Building2 className="w-5 h-5 text-orange-600" />
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Identifikace firmy</div>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Název firmy *" v={company.name} on={set("name")} testid="co-name" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="IČO" v={company.ico} on={set("ico")} testid="co-ico" />
                        <Field label="DIČ" v={company.dic} on={set("dic")} testid="co-dic" />
                    </div>
                    <Field label="Ulice a č.p." v={company.address} on={set("address")} testid="co-address" />
                    <div className="grid grid-cols-3 gap-4">
                        <Field label="PSČ" v={company.zip} on={set("zip")} />
                        <div className="col-span-2"><Field label="Město" v={company.city} on={set("city")} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Telefon" type="tel" v={company.phone} on={set("phone")} />
                        <Field label="Email" type="email" v={company.email} on={set("email")} />
                    </div>
                    <Field label="Bankovní účet (čeština)" v={company.bank_account} on={set("bank_account")} placeholder="123456789/0100" />
                    <Field label="IBAN (pro QR Platbu)" v={company.iban} on={set("iban")} testid="co-iban" placeholder="CZ6508000000192000145399" />
                    {bankError && <div className="md:col-span-2 text-xs text-red-700 bg-red-50 border border-red-300 px-3 py-2 rounded-sm" data-testid="bank-error">{bankError}</div>}
                    <div className="border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={company.vat_payer !== false} data-testid="co-vat-payer"
                                   onChange={(e) => setCompany({ ...company, vat_payer: e.target.checked })}
                                   className="w-4 h-4 accent-orange-600" />
                            <div>
                                <div className="text-sm font-semibold text-slate-900">Jsme plátci DPH</div>
                                <div className="text-xs text-slate-500">
                                    {company.vat_payer !== false
                                        ? "Faktury a rozpočty se vystavují s DPH (rychlá volba 0 / 12 / 21 %)."
                                        : "Neplátce DPH — všechny doklady se vystaví bez DPH s textem „Nejsme plátci DPH“."}
                                </div>
                            </div>
                        </label>
                        {company.vat_payer !== false && (
                            <VatRatePicker value={company.vat_rate} onChange={(v) => setCompany({ ...company, vat_rate: v })} testid="co-vat" />
                        )}
                    </div>
                </div>
            </div>

            <InvoicingSettings company={company} setCompany={setCompany} />
            <MaterialNormsCard company={company} setCompany={setCompany} />

            <DocTemplatesCard />
            <VehiclesCard />
            <TravelRatesCard />
            <BackupCard />
            <div className="mb-6" data-testid="settings-history">
                <HistoryPanel entityId={company?.id} title="Historie změn nastavení" />
                <div className="text-xs text-slate-500 mt-2">Kompletní historie všech sekcí: <Link to="/historie" className="text-orange-700 hover:underline" data-testid="settings-history-link">Historie změn</Link></div>
            </div>
            <ChangePasswordCard />

            <SmtpCard />

            <RolesTable />
        </div>
    );
}

function SmtpCard() {
    const [smtp, setSmtp] = useState(null);
    const [pw, setPw] = useState("");
    const [testTo, setTestTo] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { api.get("/company/smtp").then((r) => setSmtp(r.data)).catch(() => setSmtp(false)); }, []);
    if (smtp === null) return null;
    if (smtp === false) return null;

    const set = (k) => (e) => setSmtp({ ...smtp, [k]: e.target.value });

    const save = async () => {
        if (!smtp.smtp_host || !smtp.smtp_username) { toast.error("Vyplňte SMTP server a přihlašovací jméno"); return; }
        try {
            const { data } = await api.put("/company/smtp", {
                smtp_host: smtp.smtp_host, smtp_port: Number(smtp.smtp_port) || 465,
                smtp_username: smtp.smtp_username, smtp_password: pw || null,
                smtp_from: smtp.smtp_from || "", smtp_sender_name: smtp.smtp_sender_name || "", smtp_security: smtp.smtp_security || "ssl",
                smtp_auto_reminders: !!smtp.smtp_auto_reminders,
                smtp_weekly_summary: !!smtp.smtp_weekly_summary,
                smtp_notify_enabled: !!smtp.smtp_notify_enabled,
                smtp_notify_to: smtp.smtp_notify_to || "",
            });
            setSmtp(data);
            setPw("");
            toast.success("SMTP nastavení uloženo");
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const test = async () => {
        if (!testTo) { toast.error("Zadejte e-mail pro test"); return; }
        setBusy(true);
        try {
            await api.post("/company/smtp/test", { to: testTo });
            toast.success(`Testovací e-mail odeslán na ${testTo}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Test selhal"); }
        finally { setBusy(false); }
    };

    const applyPreset = (preset) => {
        if (preset === "seznam") setSmtp({ ...smtp, smtp_host: "smtp.seznam.cz", smtp_port: 465, smtp_security: "ssl" });
        if (preset === "gmail") setSmtp({ ...smtp, smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_security: "starttls" });
    };

    return (
        <div className="bg-white border border-slate-200 sharp-shadow mt-6" data-testid="smtp-card">
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-orange-600" />
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Odesílání e-mailů (SMTP)</div>
                </div>
                {smtp.configured && <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5">Nastaveno ✓</span>}
            </div>
            <div className="p-6 space-y-4">
                <div className="flex gap-2">
                    <button onClick={() => applyPreset("seznam")} className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-100 uppercase tracking-wider rounded-sm">Seznam.cz</button>
                    <button onClick={() => applyPreset("gmail")} className="text-xs px-3 py-1.5 border border-slate-300 hover:bg-slate-100 uppercase tracking-wider rounded-sm">Gmail</button>
                    <span className="text-xs text-slate-500 self-center ml-2">Předvyplní server a port. Gmail vyžaduje „heslo pro aplikace".</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2"><Field label="SMTP server *" v={smtp.smtp_host} on={set("smtp_host")} testid="smtp-host" placeholder="smtp.seznam.cz" /></div>
                    <Field label="Port *" type="number" v={smtp.smtp_port} on={set("smtp_port")} testid="smtp-port" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Přihlašovací jméno (e-mail) *" v={smtp.smtp_username} on={set("smtp_username")} testid="smtp-username" placeholder="info@ceramixa.cz" />
                    <div>
                        <label className="label-eyebrow block mb-1.5">Heslo {smtp.has_password ? "(uloženo — vyplňte jen pro změnu)" : "*"}</label>
                        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="smtp-password"
                               placeholder={smtp.has_password ? "••••••••" : ""}
                               className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Adresa odesílatele (volitelné)" v={smtp.smtp_from} on={set("smtp_from")} placeholder="stejné jako přihlašovací jméno" />
                    <Field label="Jméno odesílatele" v={smtp.smtp_sender_name} on={set("smtp_sender_name")} placeholder="Ceramixa Style" testid="smtp-sender-name" />
                    <div>
                        <label className="label-eyebrow block mb-1.5">Zabezpečení</label>
                        <select value={smtp.smtp_security || "ssl"} onChange={set("smtp_security")} data-testid="smtp-security"
                                className="w-full px-3 py-2 border border-slate-300 text-sm rounded-sm bg-white focus:outline-none focus:border-orange-600">
                            <option value="ssl">SSL/TLS (port 465)</option>
                            <option value="starttls">STARTTLS (port 587)</option>
                        </select>
                    </div>
                </div>
                <div className="text-xs text-slate-500 border-l-2 border-slate-300 pl-3" data-testid="reminders-note">Upomínky se neposílají automaticky – vznikají jako úkoly s vyplněnou předlohou (Šablony dokumentů níže) a odesíláte je sami.</div>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="smtp-weekly-summary">
                    <input type="checkbox" checked={!!smtp.smtp_weekly_summary}
                           onChange={(e) => setSmtp({ ...smtp, smtp_weekly_summary: e.target.checked })}
                           className="w-4 h-4 accent-orange-600" />
                    <span className="font-medium text-slate-800">Týdenní souhrn každé pondělí</span>
                    <span className="text-xs text-slate-500">(fakturace, po splatnosti, plán týdne, úkoly)</span>
                    <button onClick={async (e) => {
                        e.preventDefault();
                        try {
                            const { data } = await api.post("/company/weekly-summary/send-now");
                            toast.success(`Souhrn odeslán na ${data.sent_to}`);
                        } catch (err) { toast.error(err.response?.data?.detail || "Odeslání selhalo"); }
                    }} data-testid="weekly-summary-now"
                            className="ml-auto px-3 py-1 text-[10px] uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">
                        Poslat souhrn teď
                    </button>
                </label>
                <div className="border border-slate-200 bg-slate-50/60 p-4 space-y-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="smtp-notify-enabled">
                        <input type="checkbox" checked={!!smtp.smtp_notify_enabled}
                               onChange={(e) => setSmtp({ ...smtp, smtp_notify_enabled: e.target.checked })}
                               className="w-4 h-4 accent-orange-600" />
                        <span className="font-medium text-slate-800">Posílat mi upozornění e-mailem</span>
                        <span className="text-xs text-slate-500">(po splatnosti, konec záruky, expirace dokumentu, schválený rozpočet…)</span>
                    </label>
                    {smtp.smtp_notify_enabled && (
                        <div>
                            <label className="label-eyebrow block mb-1.5">Upozornění posílat na</label>
                            <input type="email" value={smtp.smtp_notify_to || ""} onChange={set("smtp_notify_to")} data-testid="smtp-notify-to"
                                   placeholder={smtp.smtp_username || "vas@email.cz"}
                                   className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                            <p className="text-xs text-slate-500 mt-1">Když necháte prázdné, použije se e-mail firmy. Kontrola běží automaticky každých 6 hodin.</p>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                    <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="vas@email.cz" data-testid="smtp-test-to"
                           className="flex-1 basis-[180px] min-w-0 px-3 py-2 border border-slate-300 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                    <button onClick={test} disabled={busy} data-testid="smtp-test-button"
                            className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm disabled:opacity-50">
                        {busy ? "Odesílám…" : "Odeslat testovací e-mail"}
                    </button>
                    <button onClick={save} data-testid="smtp-save"
                            className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                        Uložit SMTP
                    </button>
                </div>
            </div>
        </div>
    );
}

function ChangePasswordCard() {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 10) { toast.error("Nové heslo musí mít alespoň 10 znaků"); return; }
        if (form.new_password !== form.confirm) { toast.error("Hesla se neshodují"); return; }
        setBusy(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            toast.success("Heslo bylo změněno");
            setForm({ current_password: "", new_password: "", confirm: "" });
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Změna hesla selhala");
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="bg-white border border-slate-200 sharp-shadow mt-6" data-testid="change-password-card">
            <div className="flex items-center gap-3 px-6 py-4 border-b-2 border-slate-900">
                <KeyRound className="w-5 h-5 text-orange-600" />
                <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Změna hesla</div>
            </div>
            <div className="p-6 space-y-4">
                <Field label="Stávající heslo *" type="password" v={form.current_password} on={(e) => setForm({ ...form, current_password: e.target.value })} testid="pw-current" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Nové heslo *" type="password" v={form.new_password} on={(e) => setForm({ ...form, new_password: e.target.value })} testid="pw-new" />
                    <Field label="Nové heslo znovu *" type="password" v={form.confirm} on={(e) => setForm({ ...form, confirm: e.target.value })} testid="pw-confirm" />
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={busy} data-testid="pw-submit"
                            className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
                        <KeyRound className="w-3.5 h-3.5" /> {busy ? "Měním…" : "Změnit heslo"}
                    </button>
                </div>
            </div>
        </form>
    );
}

function Field({ label, v, on, type = "text", testid, placeholder }) {
    return (
        <div>
            <label className="label-eyebrow block mb-1.5">{label}</label>
            <input type={type} value={v || ""} onChange={on} placeholder={placeholder} data-testid={testid}
                   className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-transparent rounded-sm" />
        </div>
    );
}
